import type { ChartDataResponse } from '@gonfalon/openapi/types';

import type { ChartDataPoint } from '../types/ChartDataPoint';

/**
 * Function type for building series keys from group_by_values
 * @param args - Object containing groupByValues and groupByFieldNames
 * @param args.groupByValues - Array of group by values (e.g., ["shopping-assistant", "1"])
 * @param args.groupByFieldNames - Array of field names (e.g., ["variation_key", "version"])
 * @returns Series key string (e.g., "shopping-assistant-v1")
 */
export type BuildSeriesKeyFn = (args: { groupByValues: string[] | null; groupByFieldNames: string[] }) => string;

/**
 * Configuration options for transformChartDataResponse
 */
export interface TransformChartDataOptions {
  /**
   * The field name for the x-axis (default: 'timestamp')
   */
  xAxisDataKey?: string;
  /**
   * Custom function to build series keys from group_by_values
   */
  seriesKeyBuilder?: BuildSeriesKeyFn;
  /**
   * Additional fields to extract alongside the primary field.
   * Maps from API field name to the suffix to append to the series key.
   *
   * Example: To store raw positive/negative counts for a satisfaction chart:
   * ```typescript
   * additionalFields: {
   *   'feedback_user_positive_count': '_positive',
   *   'feedback_user_negative_count': '_negative'
   * }
   * ```
   *
   * This would create data points like:
   * ```typescript
   * {
   *   timestamp: 1234567890,
   *   'variation-v1': 85.5,  // Primary field (percentage)
   *   'variation-v1_positive': 59,  // Additional field
   *   'variation-v1_negative': 100  // Additional field
   * }
   * ```
   */
  additionalFields?: Record<string, string>;
}

/**
 * Default series key builder - just joins values with hyphens
 */
export const defaultBuildSeriesKey: BuildSeriesKeyFn = ({ groupByValues }) => {
  if (!groupByValues || groupByValues.length === 0) {
    return 'unknown';
  }
  return groupByValues.join('-');
};

/**
 * Transform API response to flat array of data points for chart rendering.
 * Groups data by timestamp and creates columns for each series combination.
 *
 * @param response - API response from /api/v2/chart/data
 * @param targetFieldName - The specific metric field to extract (e.g., 'tokens_total_sum_avg_per_generation_count')
 * @param options - Configuration options
 * @returns Array of data points with xAxis key and series columns
 */
export function transformChartDataResponse(
  response: ChartDataResponse,
  targetFieldName: string,
  options: TransformChartDataOptions = {},
): ChartDataPoint[] {
  const {
    xAxisDataKey = 'timestamp',
    seriesKeyBuilder: buildSeriesKey = defaultBuildSeriesKey,
    additionalFields = {},
  } = options;

  if (!response.items || response.items.length === 0) {
    return [];
  }

  const fieldNames = response.fields.map((field) => field.name);
  const groupByFieldNames = response.group_by_fields.map((field) => field.name);

  // Find the indices we need
  const xAxisIndex = fieldNames.indexOf(xAxisDataKey);
  const targetFieldIndex = fieldNames.indexOf(targetFieldName);

  // If required fields are missing, return empty array
  if (xAxisIndex === -1 || targetFieldIndex === -1) {
    return [];
  }

  // Find indices for additional fields
  const additionalFieldIndices: Array<{ index: number; suffix: string }> = [];
  Object.entries(additionalFields).forEach(([fieldName, suffix]) => {
    const index = fieldNames.indexOf(fieldName);
    if (index !== -1) {
      additionalFieldIndices.push({ index, suffix });
    }
  });

  // Map to collect all data points by x-axis value (typically timestamp)
  const dataByXAxis = new Map<number, ChartDataPoint>();

  // Process each series (each item represents a unique group_by combination)
  response.items.forEach((item) => {
    // Build series key using the provided function.
    // Convert group_by_values to strings because BuildSeriesKeyFn expects string[] | null,
    // while FieldValue allows number | boolean values as well.
    // Objects/arrays (e.g. JsonUnitType fields like flag_value) are JSON-stringified so
    // downstream consumers can parse them back to their native type; String() on an object
    // produces "[object Object]", which is not round-trippable.
    // Guard against null: API can return group_by_values: null (useFetchMetrics uses res.json() returning any).
    const stringGroupByValues =
      item.group_by_values && item.group_by_values.length > 0
        ? item.group_by_values.map((v) => (v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v)))
        : null;
    const seriesKey = buildSeriesKey({ groupByValues: stringGroupByValues, groupByFieldNames });

    // Process each data row for this series
    item.data.forEach((row) => {
      // Extract x-axis value. Convert to a numeric timestamp.
      // Booleans are not valid x-axis values and are skipped.
      const xAxisValue = row[xAxisIndex];
      const xAxisNumeric =
        typeof xAxisValue === 'string'
          ? new Date(xAxisValue).getTime()
          : typeof xAxisValue === 'number'
            ? xAxisValue
            : null;
      if (xAxisNumeric === null) {
        return;
      }

      // Extract the target metric value
      const metricValue = row[targetFieldIndex];
      const value = typeof metricValue === 'number' ? metricValue : null;

      // Get or create the data point for this x-axis value
      if (!dataByXAxis.has(xAxisNumeric)) {
        dataByXAxis.set(xAxisNumeric, { [xAxisDataKey]: xAxisNumeric });
      }

      const dataPoint = dataByXAxis.get(xAxisNumeric);
      if (dataPoint) {
        // Store the primary field value
        dataPoint[seriesKey] = value;

        if (value !== null) {
          additionalFieldIndices.forEach(({ index, suffix }) => {
            const additionalValue = row[index];
            // Additional fields can be any type (number, string, boolean, null)
            // They're not plotted, just stored for tooltip/custom renderer use
            dataPoint[`${seriesKey}${suffix}`] = additionalValue ?? null;
          });
        }
      }
    });
  });

  // Collect all unique series keys across all x-axis values
  const allSeriesKeys = new Set<string>();
  dataByXAxis.forEach((dataPoint) => {
    Object.keys(dataPoint).forEach((key) => {
      if (key !== xAxisDataKey) {
        allSeriesKeys.add(key);
      }
    });
  });

  // Get x-axis range from data
  const xAxisValues = Array.from(dataByXAxis.keys()).sort((a, b) => a - b);
  if (xAxisValues.length === 0) {
    return [];
  }

  // Use only existing x-axis values from the data
  const completeData = xAxisValues.map((xAxisValue) => {
    const existingData = dataByXAxis.get(xAxisValue);
    const complete: ChartDataPoint = existingData ? { ...existingData } : { [xAxisDataKey]: xAxisValue };

    // Fill in null for missing series at this x-axis value
    allSeriesKeys.forEach((seriesKey) => {
      if (!(seriesKey in complete)) {
        complete[seriesKey] = null;
      }
    });

    return complete;
  });

  return completeData;
}
